import { Typography } from '@breetometer/components';
import { ImageComponent } from 'BreetComponents';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FilePreviewError } from './Error';
import { FilePreviewLoading } from './Loading';

export const Image = ({ src }: { src: string }) => {
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [reloadKey, setReloadKey] = useState<number>(0);

	const [imageSrcToDisplay, setImageSrcToDisplay] = useState<string | null>(null);

	const isMounted = useRef(false);

	const getReloadedSourceUrl = useCallback(() => {
		if (!src) return '';
		try {
			const url = new URL(src);
			url.searchParams.set('_reload', reloadKey.toString());
			return url.toString();
		} catch (e) {
			console.warn('ImageViewer: Invalid URL provided to URL constructor, falling back to simple append:', src, e);
			return `${src}?_reload=${reloadKey}`;
		}
	}, [src, reloadKey]);

	useEffect(() => {
		isMounted.current = true;
		if (!src) {
			setLoading(false);
			setError('No image source provided.');
			return;
		}

		setLoading(true);
		setError(null);
		setImageSrcToDisplay(null);

		const loadImage = async () => {
			try {
				const response = await fetch(src);

				if (!isMounted.current) return;

				if (response.ok) {
					setImageSrcToDisplay(src);
					setLoading(false);
					setError(null);
				} else {
					console.error(`ImageViewer: Failed to fetch image. Status: ${response.status} for URL: ${src}`);
					setLoading(false);
					setError(`Failed to load image (Status: ${response.status}).`);
				}
			} catch (e: unknown) {
				if (!isMounted.current) return;
				console.error('ImageViewer: Network error during image fetch:', e, 'for URL:', src);
				setLoading(false);
				setError(`Network error: ${String(e) || 'Unknown network error'}.`);
			}
		};

		loadImage().catch(console.error);

		return () => {
			isMounted.current = false;
		};
	}, [getReloadedSourceUrl, src]);

	const onReload = useCallback(() => {
		setReloadKey((prev) => prev + 1);
	}, []);

	if (!src && !error && !loading && !imageSrcToDisplay) {
		return (
			<div>
				<Typography as='p'>No image source provided.</Typography>
			</div>
		);
	}

	if (loading) {
		return <FilePreviewLoading loadText='Loading Image' />;
	}

	if (error) {
		return (
			<FilePreviewError
				error={error}
				onReload={onReload}
			/>
		);
	}

	return (
		<div className='file-preview-wrapper_image-preview'>
			<ImageComponent
				src={imageSrcToDisplay ?? undefined}
				alt='doc'
			/>
		</div>
	);
};
